import {
  DecisionAuthority,
  GuaranteeDecision,
  GuaranteeProcessingState,
  PrescriptionDecisionWriteModel,
  PrescriptionLineView,
  V2PrescriptionModel,
} from '@lib/guarantees/data-access';

import { ServiceLineShape, isPrescriptionUnmapped } from './prescription-mapping';

/**
 * The two answers a ruling can be. `Pending` is where a line starts, never where it is put — there
 * is no route back to undecided, so it is not offered as one.
 */
export type DecisionAnswer = Exclude<GuaranteeDecision, GuaranteeDecision.Pending>;

export const DECISION_ANSWERS: DecisionAnswer[] = [GuaranteeDecision.Guaranteed, GuaranteeDecision.NotGuaranteed];

/**
 * A ruling as it is being given, before the line it answers has an id to carry it. The grounds are
 * held as the wording alone — the same as the ruling flow sends — and an empty one is a ruling not
 * yet finished rather than one given without grounds.
 *
 * Held here rather than beside either form: the line's own form collects one while the line is
 * being written, and the create-guarantee queue carries it until the record exists.
 */
export interface DecisionDraft {
  answer: DecisionAnswer;
  reason: string;
}

/**
 * The draft as the line is written with it. The grounds go as a coding carrying the wording alone,
 * exactly as the ruling flow sends them; no draft is a line left not yet ruled on.
 */
export function toDecisionWriteModel(draft: DecisionDraft | undefined): PrescriptionDecisionWriteModel | undefined {
  return draft ? { decision: draft.answer, reason: { name: draft.reason.trim() } } : undefined;
}

/**
 * Why a ruling cannot be applied to a line as it stands. Each is a fact about the line or about the
 * move, never about the manager reading it.
 */
export enum DecisionBlocker {
  /** The line is already at this answer, so the ruling would restamp who decided and when. */
  AlreadySet = 'AlreadySet',
  /**
   * Cover already given, on a record whose review is closed. Processing raises the member's task,
   * and by the time it is on their screen they may already be arranging the visit — so what they
   * were told holds.
   */
  Processed = 'Processed',
  /** Nothing to rule against. The service refuses exactly this, and only for a grant. */
  NoDiagnosis = 'NoDiagnosis',
  /** No dictionary entry behind the service, so cover granted on it could not be priced or ordered. */
  Unmapped = 'Unmapped',
  /** Not filed under any category — a grant is drawn against package options, and those are matched on categories. */
  NoCategories = 'NoCategories',
}

export const DECISION_BLOCKER_HINT_KEY: Record<DecisionBlocker, string> = {
  [DecisionBlocker.AlreadySet]: 'guaranteeV2.message.decisionAlreadySet',
  [DecisionBlocker.Processed]: 'guaranteeV2.message.decisionProcessed',
  [DecisionBlocker.NoDiagnosis]: 'guaranteeV2.message.decisionNoDiagnosis',
  [DecisionBlocker.Unmapped]: 'guaranteeV2.message.decisionUnmapped',
  [DecisionBlocker.NoCategories]: 'guaranteeV2.message.decisionNoCategories',
};

/** Whether the line is filed under at least one category — what granting cover on it needs, besides {@link allowsGrant}. */
export function hasCategories(line: { categories?: unknown[] }): boolean {
  return (line.categories?.length ?? 0) > 0;
}

/**
 * What granting cover needs of the line itself: a diagnosis to rule against — the service refuses
 * exactly that — and a service the engine can match to a dictionary entry, since a line it cannot
 * match cannot be priced, limited or ordered.
 *
 * Stated once because two screens ask it: the ruling flow, and the form that authors a line and
 * rules on it in the same pass. A form allowing what the flow refuses would be the way around the
 * rule rather than a second reading of it.
 */
export function allowsGrant(line: ServiceLineShape & { diagnosisId?: string }): boolean {
  return !!line.diagnosisId && !isPrescriptionUnmapped(line);
}

/**
 * Whether cover was granted by the rule engine rather than by a health manager. V1 said as much in
 * the wording of the grant itself; V2 records it as the decision's authority, where nothing on a row
 * reads it — so a migrated grant looks exactly like one somebody put their name to.
 *
 * Asked only of a grant. The engine commits the insurer by itself and never refuses by itself, and
 * an undecided line carries whatever authority its record was stamped with rather than a ruling.
 */
export function isAutoGuaranteed(prescription: Pick<V2PrescriptionModel, 'decision'>): boolean {
  const decision = prescription.decision;

  return decision?.decision === GuaranteeDecision.Guaranteed && decision.authority === DecisionAuthority.Auto;
}

/**
 * Whether cover already given may still be taken back. Asked of the ruling as it stands, not of the
 * one being proposed.
 */
export function allowsWithdrawal(current: GuaranteeDecision, guaranteeProcessed: boolean): boolean {
  return !(guaranteeProcessed && current === GuaranteeDecision.Guaranteed);
}

/**
 * Whether this line may be moved to this answer, and why not when it may not.
 *
 * Refusing cover stays open where granting is blocked: the grounds a grant is refused on are all
 * grounds the insurer is not committed, which refusing states rather than contradicts.
 */
export function decisionBlocker(line: PrescriptionLineView, decision: DecisionAnswer): DecisionBlocker | null {
  const { prescription } = line;
  const current = prescription.decision?.decision ?? GuaranteeDecision.Pending;

  if (current === decision) {
    return DecisionBlocker.AlreadySet;
  }

  if (!allowsWithdrawal(current, line.guaranteeStatus === GuaranteeProcessingState.Processed)) {
    return DecisionBlocker.Processed;
  }

  if (decision === GuaranteeDecision.Guaranteed && !allowsGrant(prescription)) {
    return prescription.diagnosisId ? DecisionBlocker.Unmapped : DecisionBlocker.NoDiagnosis;
  }

  if (decision === GuaranteeDecision.Guaranteed && !hasCategories(prescription)) {
    return DecisionBlocker.NoCategories;
  }

  return null;
}

/**
 * Exactly the lines an answer may be applied to. The service applies the whole batch or none of it,
 * so a blocked line left in would take every other line down with it.
 *
 * Deleted lines are passed over wherever they appear: they are not part of the care plan, and ruling
 * on one would decide something the plan no longer states.
 */
export function rulableLines(lines: PrescriptionLineView[], decision: DecisionAnswer): PrescriptionLineView[] {
  return lines.filter(line => !line.prescription.isDeleted && !decisionBlocker(line, decision));
}

/**
 * Whether a ruling is worth offering over these lines at all — true while either answer would move
 * something. A menu row that opens a flow with nothing to act on is a row that does nothing.
 */
export function isRulable(lines: PrescriptionLineView[]): boolean {
  return DECISION_ANSWERS.some(decision => rulableLines(lines, decision).length > 0);
}
